import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Document, Page } from "react-pdf";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Grid,
  Box,
  Chip,
  Stack,
  Paper,
  Pagination,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  DrawOutlined as SignatureIcon,
  InfoOutlined as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 0.75,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      textAlign="right"
      sx={{ maxWidth: "60%", wordBreak: "break-word" }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

const SignBadge = ({ label, value }) => (
  <Chip
    size="small"
    icon={
      value ? (
        <CheckCircleIcon sx={{ fontSize: 16 }} />
      ) : (
        <CancelIcon sx={{ fontSize: 16 }} />
      )
    }
    label={label}
    color={value ? "success" : "default"}
    variant={value ? "filled" : "outlined"}
    sx={{
      mr: 1,
      mb: 1,
      fontWeight: 500,
      "& .MuiChip-icon": {
        color: value ? "inherit" : "text.disabled",
      },
    }}
  />
);

const SectionHeader = ({ icon, title }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
    {icon}
    <Typography
      variant="overline"
      color="primary"
      fontWeight={700}
      sx={{ letterSpacing: 0.5 }}
    >
      {title}
    </Typography>
  </Stack>
);

const FormSettingsModal = ({
  open,
  onClose,
  form,
  onOpenDocumentSettings,
  onUpdateDocument,
}) => {
  const isSm = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [pdfFile, setPdfFile] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);

  const fetchPdf = useCallback(async (formId) => {
    setLoadingPdf(true);
    setPdfError("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/forms/download`,
        { formId, downloadType: "pdf" },
        { withCredentials: true, responseType: "blob" },
      );
      const file = new File([response.data], `${formId}.pdf`, {
        type: "application/pdf",
      });
      setPdfFile(file);
    } catch (error) {
      console.error("Failed to load PDF preview:", error);
      setPdfError("Failed to load PDF preview.");
    } finally {
      setLoadingPdf(false);
    }
  }, []);

  useEffect(() => {
    if (open && form?.form_id) {
      setPageNumber(1);
      setNumPages(0);
      setPdfFile(null);
      fetchPdf(form.form_id);
    }
    if (!open) {
      setPdfFile(null);
      setPdfError("");
    }
  }, [open, form?.form_id, fetchPdf]);

  const handleDocumentLoadSuccess = ({ numPages: n }) => {
    setNumPages(n);
  };

  const handlePageChange = (event, value) => {
    setPageNumber(value);
  };

  if (!form) return null;

  const isActive = form.status === "active";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          py: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {form.label || "Form Settings"}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
            <Chip
              size="small"
              label={isActive ? "Active" : "Inactive"}
              color={isActive ? "success" : "default"}
              sx={{ fontWeight: 600 }}
            />
            {form.doc?.is_noc && (
              <Chip size="small" label="NOC" variant="outlined" />
            )}
          </Stack>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          {onOpenDocumentSettings && (
            <Button
              onClick={() => onOpenDocumentSettings(form)}
              variant="outlined"
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Document Settings
            </Button>
          )}
          {onUpdateDocument && (
            <Button
              onClick={() => onUpdateDocument(form)}
              variant="contained"
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Update Document
            </Button>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          overflow: "hidden",
          backgroundColor: "#f5f6f8",
          p: 2,
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{ height: "100%", alignItems: "flex-start" }}
        >
          {/* PDF Preview */}
          <Grid item xs={12} sm={7} sx={{ height: isSm ? "50vh" : "100%" }}>
            <Paper
              variant="outlined"
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "#fafafa",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Preview
                </Typography>
                {numPages > 1 && (
                  <Pagination
                    count={numPages}
                    page={pageNumber}
                    onChange={handlePageChange}
                    size="small"
                  />
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: loadingPdf || pdfError ? "center" : "flex-start",
                  p: 2,
                }}
              >
                {loadingPdf && <CircularProgress size={28} />}
                {!loadingPdf && pdfError && (
                  <Typography color="error" variant="body2">
                    {pdfError}
                  </Typography>
                )}
                {!loadingPdf && !pdfError && pdfFile && (
                  <Document
                    file={pdfFile}
                    onLoadSuccess={handleDocumentLoadSuccess}
                    loading={<CircularProgress size={28} />}
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={1}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Metadata panel */}
          <Grid
            item
            xs={12}
            sm={5}
            sx={{ height: isSm ? "auto" : "100%", alignSelf: "flex-start" }}
          >
            <Paper
              variant="outlined"
              sx={{
                height: "auto",
                maxHeight: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box sx={{ overflow: "auto", p: 2.5 }}>
                <SectionHeader
                  icon={<InfoIcon fontSize="small" color="primary" />}
                  title="Form Info"
                />
                <InfoRow label="Form ID" value={form.form_id} />
                <InfoRow label="Schema Type" value={form.schema_type} />
                <InfoRow label="Added By" value={form.added_by} />

                <Box
                  sx={{ my: 2, borderTop: "1px solid", borderColor: "divider" }}
                />

                <SectionHeader
                  icon={<LocationIcon fontSize="small" color="primary" />}
                  title="Location"
                />
                <InfoRow label="County" value={form.county || "Statewide"} />
                <InfoRow
                  label="Municipality"
                  value={form.municipality || "—"}
                />
                <InfoRow
                  label="Jurisdiction ID"
                  value={form.jurisdictions_id}
                />

                <Box
                  sx={{ my: 2, borderTop: "1px solid", borderColor: "divider" }}
                />

                <SectionHeader
                  icon={<DescriptionIcon fontSize="small" color="primary" />}
                  title="Document"
                />
                <InfoRow label="Page Size" value={form.doc?.size} />
                <InfoRow label="Pages" value={form.doc?.page} />
                <InfoRow
                  label="Is NOC"
                  value={form.doc?.is_noc ? "Yes" : "No"}
                />

                <Box
                  sx={{ my: 2, borderTop: "1px solid", borderColor: "divider" }}
                />

                <SectionHeader
                  icon={<SignatureIcon fontSize="small" color="primary" />}
                  title="Signatures"
                />
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    Owner
                  </Typography>
                  <Box>
                    <SignBadge
                      label="Sign"
                      value={form.signatures?.owner?.sign}
                    />
                    <SignBadge
                      label="Notary"
                      value={form.signatures?.owner?.notary}
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    Contractor
                  </Typography>
                  <Box>
                    <SignBadge
                      label="Sign"
                      value={form.signatures?.contractor?.sign}
                    />
                    <SignBadge
                      label="Notary"
                      value={form.signatures?.contractor?.notary}
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 1.5, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormSettingsModal;
